import { money } from "./money.ts";
import { SPOT_NAME } from "./placements.ts";
import { dualPrice, printTierFor } from "./pricing.ts";
import type { PriceRule, PrintTierSetting, Spot } from "./types.ts";

// The designer page, the share page, the buyer email, the Slack note and the portal lead all word the estimate from
// here (designer-decisions.md section D): two prices, never a percentage or a name for the difference, except the
// bulk discount line (progress.md D1), whose percentage is about quantity.
export const ESTIMATE_CAPTION = "Estimate before tax and shipping. We confirm before anything prints.";
// Transfers quote their shipping (a flat amount, or free pickup), so only tax is left out.
export const TRANSFER_CAPTION = "Estimate before tax. We confirm before anything prints.";
export const BANK_LABEL = "Pay by bank";
export const CARD_LABEL = "Pay by card";

// "Pay by bank $192.71, Pay by card $198.41".
export function payLine(p: { bankCents: number; cardCents: number }): string {
  return `${BANK_LABEL} ${money(p.bankCents)}, ${CARD_LABEL} ${money(p.cardCents)}`;
}

// The quantity discount's line under the two totals (progress.md D1): "Bulk discount, 24 or more: 15% off". The one
// percentage a buyer ever sees, and it is about quantity; tests/helpers/price-words.ts lets exactly this wording through.
export function bulkDiscountLine(d: { pct: number; atCount: number }): string {
  const { atCount, pct: off } = d;
  return `Bulk discount, ${atCount} or more: ${off}% off`;
}

// What it saved, both ways to pay, for text: "$34.20 (bank) / $35.56 (card)".
export function savedPair(d: { bankCents: number; cardCents: number }): string {
  return `${money(d.bankCents)} (bank) / ${money(d.cardCents)} (card)`;
}

// The line and the saving in one sentence, for the buyer's email and the Slack note.
export function discountText(d: { pct: number; atCount: number; bankCents: number; cardCents: number }): string {
  return `${bulkDiscountLine(d)}. You save ${savedPair(d)}.`;
}

// A print's tier as a buyer reads it (spec 2026-10-07 section 1, ruling T1-a): its name, "Front center, medium", and
// the tier's two prices for one print on one garment at list ("Bank $8.08", "Card $8.32"), never the base.
export interface PrintTierPair {
  label: string;
  bankCents: number;
  cardCents: number;
}

export function printTierLabel(spot: Spot, tierName: string): string {
  return `${SPOT_NAME[spot]}, ${tierName}`;
}

// The pair in words, for the email, the receipt, the Slack note and the portal lead:
// "Front center, medium: Bank $8.08 / Card $8.32".
export function printPairText(p: PrintTierPair): string {
  return `${p.label}: Bank ${money(p.bankCents)} / Card ${money(p.cardCents)}`;
}

// Every placement's tier as a pair, in the placements' order (the server's, from settings).
export function printTierPairs(
  placements: readonly { spot: Spot; widthIn: number; heightIn: number }[],
  tiers: PrintTierSetting[],
  rule: PriceRule,
): PrintTierPair[] {
  return placements.map((p) => {
    const tier = printTierFor(p.widthIn, p.heightIn, tiers);
    return { label: printTierLabel(p.spot, tier.name), ...dualPrice(tier.cents, rule) };
  });
}

// The invoiced client order page only (one price, designer-decisions section D unchanged there, ruling T1-b):
// "Front center, medium: $8". Whole dollars without cents.
export function printTierLine(spot: Spot, tier: { name: string; cents: number }): string {
  const whole = Number.isInteger(tier.cents / 100);
  return `${printTierLabel(spot, tier.name)}: ${whole ? `$${tier.cents / 100}` : money(tier.cents)}`;
}

// Blank shipping (src/lib/pricing.ts BLANK_SHIPPING_DEFAULT): what S&S charges to ship the blanks to us, passed on
// once per order under the waiver. Plain words and the two prices, never a percentage or a name for the difference.
export const BLANK_SHIPPING_LABEL = "Blank shipping to our shop";

// "$200", or "$199.50" for a waiver in part dollars.
const dollars = (cents: number) => (Number.isInteger(cents / 100) ? `$${(cents / 100).toLocaleString("en-US")}` : money(cents));

// The short note beside it: "Free when your order is $200 or more." while charged, "Free because your order is $200 or
// more." once waived.
export function blankShippingNote(b: { bankCents: number; cardCents: number; freeAtCents: number }): string {
  return b.bankCents === 0 && b.cardCents === 0
    ? `Free because your order is ${dollars(b.freeAtCents)} or more.`
    : `Free when your order is ${dollars(b.freeAtCents)} or more.`;
}

// One line for an email, a Slack note or a portal lead:
// "Blank shipping to our shop: Bank $10.10 / Card $10.40. Free when your order is $200 or more." or, waived,
// "Blank shipping to our shop: free, because your order is $200 or more."
export function blankShippingText(b: { bankCents: number; cardCents: number; freeAtCents: number }): string {
  if (b.bankCents === 0 && b.cardCents === 0) return `${BLANK_SHIPPING_LABEL}: free, because your order is ${dollars(b.freeAtCents)} or more.`;
  return `${BLANK_SHIPPING_LABEL}: Bank ${money(b.bankCents)} / Card ${money(b.cardCents)}. ${blankShippingNote(b)}`;
}
